"use client";

import { PageHero } from "@widgets/page-hero";

import { LocationMapWithProvider } from "@features/map";
import { PointInnerContent } from "@features/point-inner-content";

import { usePointMap } from "@shared/hooks/use-point-map";
import cardsGrid from "@shared/ui/cards-grid/cards-grid.module.scss";
import { LocationMapCardLayout } from "@shared/ui/location-map-card-layout";
import { MapDetails } from "@shared/ui/map-details";
import { OtherPointsSection } from "@shared/ui/other-points-section";
import { PointCard } from "@shared/ui/point-card";

import type { MapPageType } from "@/_pages/map/model";

import type { CafesRestaurantsPointType } from "../../model/types";

import s from "./single-cafes-restaurants-point-page.module.scss";

type Props = {
  point: CafesRestaurantsPointType;
  mapData: Pick<
    MapPageType,
    "webcams" | "cafes-restaurants" | "accommodations" | "cultural-objects"
  >;
};

export const SingleCafesRestaurantsPointPage = (props: Props) => {
  const { point, mapData } = props;

  const { mapPoints, focusLocation } = usePointMap({
    mapData,
    pointCoordinates: point?.coordinates,
    focusZoom: 16,
  });

  if (!point) return null;

  const otherCafesRestaurants = point?.other_cafes_restaurants;

  return (
    <main>
      {point.title && (
        <PageHero
          variant="secondary"
          textColor="brown"
          title={point.title}
          breadcrumbs={["cafes-restaurants"]}
        />
      )}
      <PointInnerContent data={point} />
      <section id="cafes-restaurants-map" className={s.container}>
        <LocationMapCardLayout
          title="Контактная информация"
          map={
            point.coordinates && (
              <LocationMapWithProvider
                mapPoints={mapPoints}
                primaryObjectId={point.documentId}
                focusLocation={focusLocation}
              />
            )
          }
        >
          <MapDetails
            location={point.location}
            workTimeList={point.workTimeList}
            phone={point.phone}
            emails={point.email ? [point.email] : []}
          />
        </LocationMapCardLayout>
      </section>

      {otherCafesRestaurants && otherCafesRestaurants?.length > 0 && (
        <OtherPointsSection
          title="Другие кафе и рестораны"
          buttonHref="/cafes-restaurants"
          buttonText="Смотреть все"
          buttonIconName="arrow"
          cardsClassName={cardsGrid.root}
        >
          {otherCafesRestaurants?.map((card, cardIdx) =>
            card ? (
              <PointCard
                key={card?.uid ?? cardIdx}
                href={card?.uid ? `/cafes-restaurants/${card.uid}` : undefined}
                title={card?.title}
                description={card?.description}
                clampDescriptionOnMobile
                location={card?.location}
                phone={card?.phone}
                workTimeList={card?.workTimeList}
                averageCheck={card?.averageCheck}
                photos={card?.photos}
                filters={
                  card?.cafes_restaurants_types &&
                  card?.cafes_restaurants_types?.length > 0
                    ? card?.cafes_restaurants_types.map((type) => {
                        return {
                          uid: type?.uid,
                          label: type?.type,
                        };
                      })
                    : []
                }
                badges={(card?.cafes_restaurants_badge_types ?? []).map(
                  (b) => ({
                    uid: b?.uid,
                    label: b?.type,
                  }),
                )}
                overlayIcon={{ name: "food", fillColor: "gold" }}
              />
            ) : null,
          )}
        </OtherPointsSection>
      )}
    </main>
  );
};

SingleCafesRestaurantsPointPage.displayName = "SingleCafesRestaurantsPointPage";
